import {assertAndContinue} from 'Asserts';
import {GridInfo, SampleDetails} from 'constants/SampleConstants';
import {BatchInfo, BioentityInfo} from 'folder-items/FileConstants';
import makeEmptySampleDetails from 'notebook/sample-propagation/util/makeEmptySampleDetails';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  BaseTypedValueType,
  Cell,
  ColumnConstraint,
  Table,
  TableColumnType,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {
  BatchTypedValue,
  BioentityTypedValue,
  StorableInfo,
} from 'notebook/tables/typed-values/TypedValueTypes';

function extractBioentity(
  typedValue: BioentityTypedValue,
  bioentityInfo: BioentityInfo | null,
  tableName: string
): BioentityInfo | null {
  assertAndContinue(
    bioentityInfo == null,
    'Only one bioentity column should be present in a run output samples or results table',
    {tableName}
  );
  bioentityInfo = typedValue.bioentitySummary;
  return bioentityInfo;
}

function extractBatch(
  typedValue: BatchTypedValue,
  batchInfo: BatchInfo | null,
  tableName: string
): BatchInfo | null {
  assertAndContinue(
    batchInfo == null,
    'Only one batch column should be present in a run output samples or results table',
    {tableName}
  );
  batchInfo = typedValue.batchInfo;
  return batchInfo;
}

function extractContainers(
  typedValue: TypedValue,
  containers: Array<StorableInfo>,
  tableName: string
): Array<StorableInfo> {
  if (typedValue.type === BaseTypedValueType.STORABLES) {
    assertAndContinue(
      containers.length === 0,
      'Only one containers column should be present in a run output samples or results table',
      {tableName}
    );
    containers = typedValue.storableSummaries;
  }
  return containers;
}

function extractPlate(
  typedValue: TypedValue,
  plateInfo: GridInfo | null,
  tableName: string
): GridInfo | null {
  if (typedValue.type === BaseTypedValueType.STORABLES) {
    assertAndContinue(
      plateInfo == null,
      'Only one plate column should be present in a run output samples or results table',
      {tableName}
    );
    const plate = typedValue.storableSummaries[0];
    if (plate != null) {
      plateInfo = {id: plate.id, name: plate.name, barcode: plate.barcode};
    }
  }
  return plateInfo;
}

function nladStructuredTableRowToSamples(
  row: Array<Cell>,
  columnConstraints: Array<ColumnConstraint | null>,
  tableName: string
): Array<SampleDetails> {
  let batchInfo: BatchInfo | null = null;
  let bioentityInfo: BioentityInfo | null = null;
  let plateInfo: GridInfo | null = null;
  let containers: Array<StorableInfo> = [];

  for (const [colIndex, cell] of row.entries()) {
    const colConstraint = columnConstraints[colIndex];
    const typedValue = cell.typedValue;
    const colDefinition: ColumnDefinition | null | undefined = colConstraint
      ? colConstraint.columnDefinition
      : null;
    if (typedValue == null || colDefinition == null) {
      continue;
    }
    if (colDefinition.type === TableColumnType.SAMPLE) {
      if (typedValue.type === BaseTypedValueType.BIOENTITY) {
        bioentityInfo = extractBioentity(typedValue, bioentityInfo, tableName);
      } else if (typedValue.type === BaseTypedValueType.BATCH) {
        batchInfo = extractBatch(typedValue, batchInfo, tableName);
      }
    } else if (colDefinition.type === TableColumnType.PARENT_STORABLE) {
      containers = extractContainers(typedValue, containers, tableName);
    } else if (colDefinition.type === TableColumnType.PLATE) {
      plateInfo = extractPlate(typedValue, plateInfo, tableName);
    }
  }

  if (containers.length === 0) {
    return [
      {
        ...makeEmptySampleDetails(),
        bioentityInfo,
        batchInfo,
        plateInfo,
      },
    ];
  } else {
    return containers.map((containerInfo) => ({
      ...makeEmptySampleDetails(),
      bioentityInfo,
      batchInfo,
      plateInfo,
      containerInfo: {...containerInfo, barcode: containerInfo.barcode},
    }));
  }
}
export default function getSampleDetailsFromNLADStructuredTable(table: Table): Array<SampleDetails> {
  const sampleDetails: Array<SampleDetails> = [];
  const {columnConstraints} = table;

  for (const row of table.table) {
    sampleDetails.push(...nladStructuredTableRowToSamples(row, columnConstraints, table.name));
  }
  return sampleDetails;
}
